// Cache TTL+LRU en memoria, a nivel de modulo (no de componente): sobrevive
// a remounts de ProductList al navegar entre categorias/tiendas dentro de la
// misma sesion SPA (React Router no recarga la pagina). Complementa, no
// sustituye, la cache de 20s del servidor (publicResponseCache.js) -- esta
// vive en el navegador de un usuario concreto y evita el viaje de red
// entero (no solo el trabajo de Postgres) al volver a un filtro ya visto o
// al entrar en una pagina cuyo primer resultado ya se prefetcheo al pasar
// el raton por el enlace.
const DEFAULT_TTL_MS = 60000;
const DEFAULT_MAX_ENTRIES = 60;

function createRequestCache({ ttlMs = DEFAULT_TTL_MS, maxEntries = DEFAULT_MAX_ENTRIES } = {}) {
    const entries = new Map();
    const pending = new Map();

    function get(key) {
        const entry = entries.get(key);
        if (!entry) return undefined;
        if (Date.now() > entry.expiresAt) {
            entries.delete(key);
            return undefined;
        }
        // Reinsertar mueve la clave al final del Map -- el orden de
        // insercion hace de orden LRU sin estructura aparte.
        entries.delete(key);
        entries.set(key, entry);
        return entry.value;
    }

    function set(key, value) {
        entries.delete(key);
        entries.set(key, { value, expiresAt: Date.now() + ttlMs });
        if (entries.size > maxEntries) {
            const oldestKey = entries.keys().next().value;
            entries.delete(oldestKey);
        }
    }

    // Evita prefetches duplicados si el usuario pasa el raton por varios
    // enlaces al mismo destino antes de que el primero termine.
    function prefetch(key, loader) {
        if (get(key) !== undefined || pending.has(key)) return;
        const promise = loader()
            .then((value) => {
                set(key, value);
            })
            .catch(() => {})
            .finally(() => {
                pending.delete(key);
            });
        pending.set(key, promise);
    }

    function clear() {
        entries.clear();
        pending.clear();
    }

    return { get, set, prefetch, clear };
}

export const dealsRequestCache = createRequestCache();
export const facetsRequestCache = createRequestCache();

const PAGE_SIZE = 50;

// Misma logica de construccion de query que ProductList.js para el primer
// fetch (page=0, sin busqueda/atributos) -- usada tanto por el efecto
// principal como por el prefetch on-hover, para garantizar que ambos
// generan EXACTAMENTE la misma clave de cache.
export function buildDealsListUrl({ category = '', store = '', sort = 'recent' } = {}) {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: '0', sort });
    if (category) params.set('category', category);
    if (store) params.set('store', store);
    return `/api/deals?${params.toString()}`;
}

export function buildFacetsUrl({ category = '', store = '' } = {}) {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (store) params.set('store', store);
    const query = params.toString();
    return query ? `/api/deals/facets?${query}` : '/api/deals/facets';
}

// Prefetch on-hover/focus para enlaces de categoria/tienda: solo cubre el
// caso comun (listado por defecto, sin busqueda/filtros de atributo todavia
// elegidos) porque es el que de verdad se puede predecir antes del click.
export function prefetchDealsList({ category = '', store = '' } = {}) {
    if (!category && !store) return;
    const dealsUrl = buildDealsListUrl({ category, store });
    dealsRequestCache.prefetch(dealsUrl, () => fetch(dealsUrl).then((res) => (res.ok ? res.json() : Promise.reject(new Error('prefetch-failed')))));

    const facetsUrl = buildFacetsUrl({ category, store });
    facetsRequestCache.prefetch(facetsUrl, () => fetch(facetsUrl).then((res) => (res.ok ? res.json() : Promise.reject(new Error('prefetch-failed')))));
}
